/**
 * cy-bridge: a small, dependency-free RPC + event channel between a host page
 * and an iframe (or opener/popup). Replaces the Penpal 3 fork.
 *
 * Wire protocol (contract v1)
 *
 *   window.postMessage — handshake only:
 *     { cy: 1, t: 'hello', methods: string[], contract: number }
 *     { cy: 1, t: 'ack',   methods: string[], contract: number }  (+ transferred MessagePort)
 *
 *   MessagePort — everything after the handshake:
 *     { t: 'call',    id, m, a }        request
 *     { t: 'ret',     id, ok, v }       response; v is the value, or {name,message,code} when !ok
 *     { t: 'emit',    e, p }            fire-and-forget event
 *     { t: 'methods', m: string[] }     the peer added methods after the handshake
 *
 * Either side may start. The parent owns the MessageChannel: when it receives a
 * `hello` it replies `ack` carrying port2; a child that receives a parent `hello`
 * simply re-sends its own `hello`. A child that reloads sends a fresh `hello`,
 * the parent swaps ports and emits `reconnect`.
 *
 * Legacy Penpal 3 fallback (child side only, `legacy: true`)
 *
 *   Old SCORM exports still load Penpal 3 from unpkg and act as the parent. The
 *   child additionally sends `{ penpal: 'handshake', methodNames }`; if the parent
 *   answers `{ penpal: 'handshake-reply', methodNames }` the bridge switches to
 *   Penpal's window.postMessage call/reply framing for that connection.
 */

const KEY = 'cy';
const VERSION = 1;

const PENPAL = {
  HANDSHAKE: 'handshake',
  HANDSHAKE_REPLY: 'handshake-reply',
  CALL: 'call',
  REPLY: 'reply',
  FULFILLED: 'fulfilled',
  REJECTED: 'rejected',
};

export class BridgeError extends Error {
  constructor(message, code, extra = {}) {
    super(message);
    this.name = 'BridgeError';
    this.code = code;
    Object.assign(this, extra);
  }
}

const serializeError = (e) => ({
  name: (e && e.name) || 'Error',
  message: (e && e.message) || String(e),
  code: e && e.code,
  data: e && e.data,
});

const toError = (o) => {
  const err = new Error(o && o.message);
  if (o) {
    err.name = o.name || 'Error';
    if (o.code !== undefined) err.code = o.code;
    if (o.data !== undefined) err.data = o.data;
  }
  return err;
};

/**
 * @typedef {object} Bridge
 * @property {Promise<Record<string, Function>>} promise resolves with the remote API once connected
 * @property {Record<string, Function>} remote live proxy of the peer's methods
 * @property {{methods: string[], contract: number, legacy: boolean}} peer
 * @property {boolean} connected
 * @property {(event: string, payload?: any) => void} emit send a fire-and-forget event to the peer
 * @property {(event: string, cb: Function) => () => void} on listen for peer events / lifecycle events
 * @property {(methods: Record<string, Function>) => void} addMethods expose more methods after creation
 * @property {() => void} destroy
 */

/**
 * @param {object} opts
 * @param {Window} opts.remote            window to talk to (iframe.contentWindow, window.parent, window.opener)
 * @param {string} opts.origin            exact origin of `remote`. Children may pass '*' to lock onto the
 *                                        origin seen during the handshake (frame-ancestors is the trust anchor).
 * @param {boolean} [opts.parent=false]   true on the host side (owns the MessageChannel)
 * @param {Record<string, Function>} [opts.methods]
 * @param {number} [opts.contract=1]      contract version advertised in the handshake
 * @param {number} [opts.timeout=0]       ms before `promise` rejects; 0 disables
 * @param {boolean} [opts.legacy=false]   child only: also answer Penpal 3 parents
 * @param {Window} [opts.local=window]
 * @returns {Bridge}
 */
export function createBridge({
  remote,
  origin,
  parent = false,
  methods = {},
  contract = VERSION,
  timeout = 0,
  legacy = false,
  local = typeof window !== 'undefined' ? window : undefined,
}) {
  if (!remote) {
    throw new BridgeError('cy-bridge: remote window is required', 'NO_REMOTE');
  }
  if (!origin || typeof origin !== 'string') {
    throw new BridgeError('cy-bridge: origin is required', 'NO_ORIGIN');
  }
  if (origin === '*' && parent) {
    throw new BridgeError(
      'cy-bridge: a parent must know its child origin',
      'NO_ORIGIN'
    );
  }

  const localMethods = { ...methods };
  const remoteApi = {};
  const pending = new Map();
  const listeners = new Map();

  let targetOrigin = origin;
  let transport = null; // { post(msg), close() }
  let seq = 0;
  let destroyed = false;
  let peer = { methods: [], contract: 0, legacy: false };

  // Handshake nonce. Both sides may say hello at the same time and a child
  // answers a parent hello with its own hello, so a parent can see the same
  // hello twice; it only acks a nonce once. Random per instance so a reloaded
  // child is always treated as new.
  let nonce = Math.random().toString(36).slice(2) + ':' + 0;
  let helloGeneration = 0;
  let lastAckedNonce = null;

  const emitLocal = (event, payload) => {
    (listeners.get(event) || []).slice().forEach((cb) => {
      try {
        cb(payload);
      } catch (e) {
        // listener errors must never break the channel
        setTimeout(() => {
          throw e;
        });
      }
    });
  };

  let resolveReady;
  let rejectReady;
  const promise = new Promise((res, rej) => {
    resolveReady = res;
    rejectReady = rej;
  });
  // avoid unhandled rejection noise when the consumer only uses `on()`
  promise.catch(() => {});

  const timer =
    timeout > 0
      ? setTimeout(() => {
          if (!transport)
            fail(new BridgeError('cy-bridge: handshake timed out', 'TIMEOUT'));
        }, timeout)
      : null;

  const methodNames = () => Object.keys(localMethods);

  const postToRemote = (msg, transfer) => {
    try {
      remote.postMessage(msg, targetOrigin, transfer || []);
    } catch (e) {
      // remote may be gone (closed popup, removed iframe)
    }
  };

  // A parent usually builds its bridge right after inserting the iframe, while
  // the frame is still `about:blank` (same-origin with the host). Posting to it
  // with the child origin only logs a browser warning, and the child says hello
  // itself once it loads, so there is nothing to send yet. A cross-origin
  // access error means the frame has navigated.
  const remoteNotLoaded = () => {
    if (!parent) return false;
    try {
      return remote.location.href === 'about:blank';
    } catch (e) {
      return false;
    }
  };

  let helloQueued = false;
  // Coalesce: consumers often create the bridge and addMethods() in the same
  // tick; one hello per microtask keeps the peer from re-acking repeatedly.
  const hello = () => {
    if (helloQueued || destroyed) return;
    helloQueued = true;
    Promise.resolve().then(() => {
      helloQueued = false;
      if (destroyed || remoteNotLoaded()) return;
      postToRemote({
        [KEY]: VERSION,
        t: 'hello',
        n: nonce,
        methods: methodNames(),
        contract,
      });
      if (legacy && !parent) {
        postToRemote({ penpal: PENPAL.HANDSHAKE, methodNames: methodNames() });
      }
    });
  };

  const send = (msg) => {
    if (transport) transport.post(msg);
  };

  const bindRemote = (names) => {
    Object.keys(remoteApi).forEach((k) => delete remoteApi[k]);
    names.forEach((m) => {
      remoteApi[m] = (...a) =>
        new Promise((res, rej) => {
          if (destroyed || !transport) {
            return rej(
              new BridgeError(
                `cy-bridge: cannot call ${m}(), not connected`,
                'DESTROYED'
              )
            );
          }
          const id = ++seq;
          pending.set(id, [res, rej]);
          send({ t: 'call', id, m, a });
        });
    });
  };

  const handleMessage = async (data) => {
    if (!data || typeof data !== 'object') return;

    if (data.t === 'call') {
      const fn = localMethods[data.m];
      let ok = true;
      let v;
      try {
        if (typeof fn !== 'function') {
          throw new BridgeError(`cy-bridge: no method ${data.m}`, 'NO_METHOD');
        }
        v = await fn(...(data.a || []));
      } catch (e) {
        ok = false;
        v = serializeError(e);
      }
      try {
        send({ t: 'ret', id: data.id, ok, v });
      } catch (e) {
        // DataCloneError etc: still settle the caller
        send({ t: 'ret', id: data.id, ok: false, v: serializeError(e) });
      }
      return;
    }

    if (data.t === 'ret') {
      const p = pending.get(data.id);
      if (!p) return;
      pending.delete(data.id);
      if (data.ok) p[0](data.v);
      else p[1](toError(data.v));
      return;
    }

    if (data.t === 'emit') {
      emitLocal(data.e, data.p);
      return;
    }

    if (data.t === 'methods' && Array.isArray(data.m)) {
      peer = { ...peer, methods: data.m };
      bindRemote(data.m);
      emitLocal('methods', peer);
    }
  };

  const adopt = (nextTransport, meta, isLegacy) => {
    const reconnect = !!transport;
    if (transport) transport.close();
    transport = nextTransport;
    peer = {
      methods: meta.methods || [],
      contract: meta.contract || 0,
      legacy: !!isLegacy,
    };
    bindRemote(peer.methods);
    if (timer) clearTimeout(timer);
    resolveReady(remoteApi);
    emitLocal(reconnect ? 'reconnect' : 'connect', peer);
  };

  const portTransport = (port) => {
    const onPortMessage = (e) => handleMessage(e.data);
    port.addEventListener('message', onPortMessage);
    if (port.start) port.start();
    return {
      post: (msg) => port.postMessage(msg),
      close: () => {
        port.removeEventListener('message', onPortMessage);
        port.close();
      },
    };
  };

  // Penpal 3 framing over window.postMessage
  const legacyTransport = () => ({
    post: (msg) => {
      if (msg.t === 'call') {
        postToRemote({
          penpal: PENPAL.CALL,
          id: msg.id,
          methodName: msg.m,
          args: msg.a,
        });
      } else if (msg.t === 'ret') {
        postToRemote({
          penpal: PENPAL.REPLY,
          id: msg.id,
          resolution: msg.ok ? PENPAL.FULFILLED : PENPAL.REJECTED,
          returnValue: msg.v,
          returnValueIsError: !msg.ok,
        });
      }
      // emit / methods have no Penpal 3 equivalent
    },
    close: () => {},
  });

  const fromLegacy = (d) => {
    if (d.penpal === PENPAL.CALL) {
      return { t: 'call', id: d.id, m: d.methodName, a: d.args || [] };
    }
    if (d.penpal === PENPAL.REPLY) {
      return {
        t: 'ret',
        id: d.id,
        ok: d.resolution === PENPAL.FULFILLED,
        v: d.returnValue,
      };
    }
    return null;
  };

  const originMatches = (e) =>
    targetOrigin === '*' || e.origin === targetOrigin;

  const onWindowMessage = (e) => {
    const d = e.data;
    if (!d || typeof d !== 'object' || e.source !== remote || !originMatches(e))
      return;

    if (d[KEY] === VERSION) {
      if (d.t === 'hello') {
        if (parent) {
          if (d.n && d.n === lastAckedNonce) return; // duplicate of a hello we already answered
          lastAckedNonce = d.n || null;
          const ch = new MessageChannel();
          postToRemote(
            {
              [KEY]: VERSION,
              t: 'ack',
              n: d.n,
              methods: methodNames(),
              contract,
            },
            [ch.port2]
          );
          adopt(portTransport(ch.port1), d, false);
        } else {
          // the parent (re)started: make sure it hears about us
          lockOrigin(e.origin);
          hello();
        }
      } else if (d.t === 'ack' && !parent && e.ports && e.ports[0]) {
        if (d.n && d.n !== nonce) return; // answer to a stale hello
        lockOrigin(e.origin);
        adopt(portTransport(e.ports[0]), d, false);
      }
      return;
    }

    if (legacy && !parent && d.penpal) {
      if (d.penpal === PENPAL.HANDSHAKE_REPLY) {
        lockOrigin(e.origin);
        adopt(
          legacyTransport(),
          { methods: d.methodNames || [], contract: 0 },
          true
        );
      } else if (transport && peer.legacy) {
        const msg = fromLegacy(d);
        if (msg) handleMessage(msg);
      }
    }
  };

  const lockOrigin = (seen) => {
    if (targetOrigin === '*' && seen && seen !== 'null') {
      targetOrigin = seen;
    }
  };

  const fail = (err) => {
    rejectReady(err);
    destroy();
  };

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    if (timer) clearTimeout(timer);
    local.removeEventListener('message', onWindowMessage);
    if (transport) transport.close();
    transport = null;
    pending.forEach(([, rej]) =>
      rej(new BridgeError('cy-bridge: destroyed', 'DESTROYED'))
    );
    pending.clear();
    rejectReady(new BridgeError('cy-bridge: destroyed', 'DESTROYED'));
    emitLocal('destroy');
    listeners.clear();
  };

  local.addEventListener('message', onWindowMessage);
  hello();

  return {
    promise,
    remote: remoteApi,
    get peer() {
      return peer;
    },
    get connected() {
      return !!transport && !destroyed;
    },
    get origin() {
      return targetOrigin;
    },
    emit: (e, p) => send({ t: 'emit', e, p }),
    on(e, cb) {
      const arr = listeners.get(e) || [];
      arr.push(cb);
      listeners.set(e, arr);
      return () =>
        listeners.set(
          e,
          (listeners.get(e) || []).filter((x) => x !== cb)
        );
    },
    addMethods(more = {}) {
      Object.assign(localMethods, more);
      if (transport) {
        send({ t: 'methods', m: methodNames() });
      } else {
        // a fresh nonce so the peer acks the hello carrying the new list
        nonce = nonce.split(':')[0] + ':' + ++helloGeneration;
        hello();
      }
    },
    destroy,
  };
}

export const BRIDGE_VERSION = VERSION;
